import { useEffect, useRef, useState } from 'react'
import { Bell, Clock, ExternalLink, RefreshCw, X } from 'lucide-react'
import { Button, cx } from '@/components/ui/primitives'
import { BUILD } from '@/lib/build'
import { updateNotice } from '@/lib/buildCompare'
import { useAuth } from '@/lib/auth'
import { plainPreview } from '@/lib/markdown'
import { useAppVersion } from '@/lib/appVersion'
import { track } from '@/lib/analytics'
import { urlForNotification, type UrlState } from '@/lib/urlState'
import { TIME_EXPORT, useTimeExportNotice } from '@/lib/timeExport'
import { clearNotification, clearable, markAllRead, markRead, useNotifications } from './useNotifications'

const when = (ts: { seconds: number } | null) =>
  ts ? new Date(ts.seconds * 1000).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : ''

export function NotificationBell({ onNavigate }: {
  /** Same shape the search palette uses, so a mention and a search result open an item the same way. */
  onNavigate: (next: Partial<UrlState>) => void
}) {
  const { user, isAdmin } = useAuth()
  const { items, unread } = useNotifications(user?.uid)
  // A tab open since before a deploy keeps running what it loaded, and nobody reloads a tab
  // that looks fine — which is how somebody spends an afternoon reporting bugs that were
  // fixed that morning. It counts towards the bell so it is seen without opening it.
  const { updateAvailable, latest } = useAppVersion()
  // The time export reaches the tracker through a deploy, but only becomes the hours
  // everybody reads when somebody presses Load. Nothing asked them to — which is the same
  // silence that let the figure drift in the first place.
  const { pending: exportPending, publishedHours } = useTimeExportNotice()
  const timeExportDue = isAdmin && exportPending
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  if (!user) return null

  // Two of the three things this bell reports are not documents anybody wrote — they are
  // conditions the tab works out for itself. Counting them together is what stops a real
  // mention hiding behind a notice, or the other way round.
  const badge = unread + (updateAvailable ? 1 : 0) + (timeExportDue ? 1 : 0)

  return (
    <div className="relative" ref={ref}>
      <Button
        onClick={() => setOpen((o) => !o)}
        aria-label={
          updateAvailable ? 'A new version is available'
          : timeExportDue ? 'A time export is waiting to be imported'
          : unread > 0 ? `${unread} unread notifications`
          : 'Notifications'
        }
        aria-expanded={open}
        className="relative"
      >
        <Bell size={15} aria-hidden />
        {badge > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-mc-accent px-1 text-[10px] font-bold text-black">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-[min(92vw,22rem)] overflow-hidden rounded-lg border border-mc-line bg-mc-surface shadow-xl">
          <div className="flex items-center justify-between border-b border-mc-line px-3 py-2">
            <span className="text-xs font-bold tracking-wide text-mc-muted uppercase">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                className="text-xs text-mc-primary hover:underline"
                onClick={() => void markAllRead(user.uid, items.filter((n) => !n.read).map((n) => n.id))}
              >
                Mark all read
              </button>
            )}
          </div>
          {updateAvailable && (
            <div className="border-b border-mc-line bg-mc-medium-soft px-3 py-2.5">
              <p className="text-sm font-medium text-mc-ink">A new version is available</p>
              <p className="mt-0.5 text-xs text-mc-muted">
                {updateNotice(BUILD, latest)} Refresh to pick it up — nothing
                you have typed anywhere else is affected.
              </p>
              <Button variant="primary" className="mt-2"
                      onClick={() => window.location.reload()}>
                <RefreshCw size={14} aria-hidden /> Refresh now
              </Button>
            </div>
          )}
          {timeExportDue && (
            <div className="border-b border-mc-line bg-mc-medium-soft px-3 py-2.5">
              <p className="flex items-center gap-1.5 text-sm font-medium text-mc-ink">
                <Clock size={14} aria-hidden /> A time export is waiting to be imported
              </p>
              <p className="mt-0.5 text-xs text-mc-muted">
                This build carries {TIME_EXPORT.entries} entries totalling{' '}
                {TIME_EXPORT.hours} hours
                {TIME_EXPORT.latest ? `, up to ${TIME_EXPORT.latest}` : ''}. The tracker is
                showing{' '}
                {publishedHours === null || publishedHours === undefined
                  ? 'nothing yet'
                  : `${publishedHours}`}
                . Load it from Admin → Time.
              </p>
            </div>
          )}
          <ul className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 && !updateAvailable && !timeExportDue && (
              <li className="px-3 py-6 text-center text-sm text-mc-muted">Nothing yet.</li>
            )}
            {items.map((n) => {
              // Worked out once per row so the label can promise only what the click does.
              // A mention stored with a kind nothing recognises still marks itself read;
              // it just does not pretend to go anywhere.
              const target = urlForNotification(n)
              return (
              <li key={n.id} className={cx(
                'relative border-b border-mc-line last:border-0',
                !n.read && 'bg-mc-medium-soft',
              )}>
                <button
                  type="button"
                  onClick={() => {
                    void markRead(user.uid, n.id)
                    if (target) {
                      track('notification_opened', { kind: n.itemType })
                      onNavigate(target)
                    }
                    setOpen(false)
                  }}
                  aria-label={target
                    ? `Open ${n.itemRef}: ${n.itemTitle}`
                    : `Notification about ${n.itemRef}, which can no longer be opened`}
                  className={cx(
                    'w-full px-3 py-2.5 text-left hover:bg-mc-surface-2',
                    clearable(n) && 'pr-9',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-mc-primary">{n.itemRef}</span>
                    {/* Only where the click actually goes somewhere. An icon on a row that
                        opens nothing would be the promise this is meant to make honest. */}
                    {target && <ExternalLink size={11} className="text-mc-muted" aria-hidden />}
                    {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-mc-accent" aria-hidden />}
                    <span className="ml-auto text-[11px] text-mc-muted">{when(n.at)}</span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm text-mc-body">{plainPreview(n.body)}</p>
                  <p className="mt-0.5 text-xs text-mc-muted">
                    {n.kind === 'reaction'
                      ? `${n.byName ?? n.byEmail} reacted ${n.emoji ?? ''}`.trim()
                      : `${n.byName ?? n.byEmail} mentioned you`}
                  </p>
                </button>
                {/* A reaction has said all it has to say once it has been seen. A mention
                    is a question, and nothing else here lists what you were asked, so it
                    stays until it is dealt with rather than being sweepable. */}
                {clearable(n) && (
                  <button
                    type="button"
                    onClick={() => void clearNotification(user.uid, n.id)}
                    aria-label={`Clear the reaction on ${n.itemRef}`}
                    className="absolute top-2 right-2 rounded p-1 text-mc-muted hover:bg-mc-surface-2 hover:text-mc-ink"
                  >
                    <X size={13} aria-hidden />
                  </button>
                )}
              </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
